import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/cn";

type TrendPoint = {
  month: string;
  label: string;
  co: number;
  voc: number;
  total: number;
};

/**
 * Compact month-by-month exposure chart. Two distinct, non-stacked series:
 *
 *  • CARCINOGEN (purple) — `total`: firefighter-exposures with elevated TOTAL VOC
 *    that month (the carcinogen indicator; one count per firefighter per incident).
 *    Total VOC is the unspeciated aromatic load — not a benzene measurement.
 *  • ACUTE CO (blue) — `co`: exposures where CO went over its NIOSH REL that month
 *    (carbon-monoxide poisoning is an acute hazard, not a carcinogen).
 *
 * They are separate hazards, so the bars are grouped side-by-side, never stacked.
 * A count — not a summed %REL — keeps the unit interpretable for a chief. Sized to
 * sit in the KPI grid; when `to` is set the whole card is a drill-down link.
 */
export function MonthlyTrendsCard({
  data,
  to,
  chartClassName = "h-16",
}: {
  data: TrendPoint[];
  to?: string;
  chartClassName?: string;
}) {
  const hasData = data.some((d) => d.total > 0 || d.co > 0);
  const card = (
    <Card
      className={cn(
        "p-0",
        to &&
          "h-full transition-colors group-hover:border-primary/50 group-hover:shadow-sm",
      )}
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          Carcinogen exposure (total VOC) + acute CO
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className={cn("w-full", chartClassName)}>
          {hasData ? (
            <ResponsiveContainer>
              <BarChart
                data={data}
                margin={{ top: 4, right: 4, bottom: 0, left: 4 }}
              >
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                  interval={0}
                />
                <Tooltip
                  cursor={{ fill: "rgba(0,0,0,0.05)" }}
                  labelFormatter={(label) => String(label)}
                  formatter={(value, name) => [
                    `${value} ${value === 1 ? "exposure" : "exposures"}`,
                    String(name),
                  ]}
                />
                {/* Brand categorical pair (red-500 / blue-500). Validated as
                    the only two-series set the palette supports: ΔE 35.3
                    normal-vision, 29.9 worst-case CVD, passing in both light
                    and dark. Series identity is also carried by the legend and
                    tooltip labels, never by colour alone. */}
                <Bar
                  dataKey="total"
                  name="Carcinogen: total VOC elevated"
                  fill="#D5312C"
                  radius={[2, 2, 0, 0]}
                  isAnimationActive={false}
                />
                <Bar
                  dataKey="co"
                  name="Acute: CO over REL"
                  fill="#4C8DFF"
                  radius={[2, 2, 0, 0]}
                  isAnimationActive={false}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-full items-center text-xs text-muted-foreground">
              No elevated VOC or acute CO in the last 6 months.
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
  if (!to) return card;
  return (
    <Link
      to={to}
      aria-label="Carcinogen exposure (total VOC) and acute CO"
      className="group block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      {card}
    </Link>
  );
}
